import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Partner } from '../types/Types';
import axios from 'axios';

const DEFAULT_SEARCH_LIMIT = 5;

interface CachedPartner {
  data: Partner;
  timestamp: number;
}

interface CachedSearchResult {
  partners: Partner[];
  timestamp: number;
}

interface PartnerCacheContextType {
  // Cache management
  getPartner: (id: string) => Partner | null;
  ensurePartner: (id: string) => Promise<Partner | null>;
  addPartnersToCache: (partners: Partner[]) => void;
  preloadPartnersForRows: (rows: any[]) => Promise<void>;
  clearCache: () => void; // Clear all cached data
  isLoading: boolean;
  isPreloading: boolean;

  // Search functionality with infinite scroll
  searchPartners: (query: string, page?: number, stockManagement?: boolean, limit?: number) => Promise<{ partners: Partner[]; hasMore: boolean }>;

  // Initialize with getPartnerById function
  initialize: (getPartnerByIdFn: (id: string) => Promise<any>) => void;
}

const PartnerCacheContext = createContext<PartnerCacheContextType | undefined>(undefined);

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

export const PartnerCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const partnerCache = useRef<Map<string, CachedPartner>>(new Map());
  const searchResultsCache = useRef<Map<string, CachedSearchResult>>(new Map());
  const inflightById = useRef<Map<string, Promise<Partner | null>>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [isPreloading, setIsPreloading] = useState(false);
  const [getPartnerByIdFn, setGetPartnerByIdFn] = useState<((id: string) => Promise<any>) | null>(null);

  const initialize = useCallback((getPartnerByIdFn: (id: string) => Promise<any>) => {
    setGetPartnerByIdFn(() => getPartnerByIdFn);
  }, []);

  const getPartner = useCallback((id: string): Partner | null => {
    const cached = partnerCache.current.get(id);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp >= CACHE_TTL) {
      // Cache expired, remove it
      partnerCache.current.delete(id);
      return null;
    }

    return cached.data;
  }, []);

  const addPartnersToCache = useCallback((partners: Partner[]) => {
    const now = Date.now();
    partners.forEach(partner => {
      if (partner && partner._id) {
        partnerCache.current.set(partner._id, {
          data: partner,
          timestamp: now
        });
      }
    });
  }, []);

  // Cache-first single-partner resolution. Callers that need a business decision from a
  // partner field (domestic/VAT, currency, payment terms) must use this instead of getPartner:
  // getPartner is a synchronous cache read and silently returns null on a miss, which reads as
  // "domestic" and produced 18% VAT rows for foreign buyers until the server corrected them on save.
  const ensurePartner = useCallback(async (id: string): Promise<Partner | null> => {
    if (!id) return null;

    const cached = getPartner(id);
    if (cached) return cached;

    const pending = inflightById.current.get(id);
    if (pending) return pending;

    const request = (async (): Promise<Partner | null> => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL ?? '';
        const token = localStorage.getItem('token');
        const response = await axios.get<Partner>(`${apiUrl}/api/partners/${id}`, {
          headers: { 'x-auth-token': token || '' },
        });
        const partner = response.data;
        if (partner && partner._id) {
          addPartnersToCache([partner]);
          return partner;
        }
        return null;
      } catch {
        return null;
      } finally {
        inflightById.current.delete(id);
      }
    })();

    inflightById.current.set(id, request);
    return request;
  }, [getPartner, addPartnersToCache]);

  const searchPartners = useCallback(async (query: string, page: number = 1, stockManagement: boolean = false, limit: number = DEFAULT_SEARCH_LIMIT): Promise<{ partners: Partner[]; hasMore: boolean }> => {
    const cacheKey = `${query.toLowerCase().trim()}_page_${page}_stock_${stockManagement}_lim_${limit}`;
    const now = Date.now();

    // Return cached result if available and not expired
    const cachedResult = searchResultsCache.current.get(cacheKey);
    if (cachedResult && (now - cachedResult.timestamp < CACHE_TTL)) {
      return {
        partners: cachedResult.partners,
        hasMore: cachedResult.partners.length === limit // If we got a full page, assume there might be more
      };
    }

    // Remove expired cache entry
    if (cachedResult && (now - cachedResult.timestamp >= CACHE_TTL)) {
      searchResultsCache.current.delete(cacheKey);
    }

    // Don't make API calls if already loading
    if (isLoading) {
      return { partners: [], hasMore: false };
    }

    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL ?? '';
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      // Choose endpoint based on stockManagement flag
      const endpoint = stockManagement
        ? `${apiUrl}/api/partners/stock-managment`
        : `${apiUrl}/api/partners/active`;

      const params: any = {
        search: query,
        sort: 'name',
        order: 'asc',
        page,
        limit,
      };

      const response = await axios.get(endpoint, {
        headers: { 'x-auth-token': token },
        params,
      });

      const newPartners: Partner[] = response.data.partners || [];
      const totalPages = response.data.totalPages || 1;
      const hasMore = page < totalPages;

      // Cache the result with TTL
      searchResultsCache.current.set(cacheKey, {
        partners: newPartners,
        timestamp: now
      });

      // Cache individual partners with TTL
      newPartners.forEach(partner => {
        if (partner && partner._id) {
          partnerCache.current.set(partner._id, {
            data: partner,
            timestamp: now
          });
        }
      });

      return { partners: newPartners, hasMore };

    } catch (error) {
      return { partners: [], hasMore: false };
    } finally {
      setIsLoading(false);
    }
  }, [isLoading]);

  const preloadPartnersForRows = useCallback(async (rows: any[]) => {
    if (!rows?.length) return;

    // Extract unique partner IDs that aren't cached or are expired
    const uncachedIds = new Set<string>();
    const now = Date.now();

    rows.forEach(row => {
      // Check multiple possible partner fields
      const partnerFields = ['supplier', 'buyer', 'payer', 'carrier', 'partner'];
      partnerFields.forEach(field => {
        const partnerId = row[field]?._id;
        if (partnerId) {
          const cached = partnerCache.current.get(partnerId);
          // Add to uncached if not in cache or cache expired
          if (!cached || (now - cached.timestamp >= CACHE_TTL)) {
            uncachedIds.add(partnerId);
          }
        }
      });
    });

    if (uncachedIds.size === 0) return;

    setIsPreloading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const apiUrl = process.env.REACT_APP_API_URL ?? '';
      const partnerIds = Array.from(uncachedIds);

      // Use bulk-ids endpoint for SINGLE API call instead of multiple individual calls
      const response = await fetch(`${apiUrl}/api/partners/bulk-ids`, {
        method: 'POST',
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: partnerIds })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.partners && data.partners.length > 0) {
          // Add all partners to cache at once with timestamps
          const timestamp = Date.now();
          data.partners.forEach((partner: Partner) => {
            if (partner && partner._id) {
              partnerCache.current.set(partner._id, {
                data: partner,
                timestamp
              });
            }
          });
        }
      }
    } catch (error) {
    } finally {
      setIsPreloading(false);
    }
  }, []);

  const clearCache = useCallback(() => {
    // Clear all cached data
    partnerCache.current.clear();
    searchResultsCache.current.clear();
  }, []);

  const value: PartnerCacheContextType = {
    getPartner,
    ensurePartner,
    addPartnersToCache,
    preloadPartnersForRows,
    clearCache,
    isLoading,
    isPreloading,
    searchPartners,
    initialize,
  };

  return <PartnerCacheContext.Provider value={value}>{children}</PartnerCacheContext.Provider>;
};

export const usePartnerCache = (): PartnerCacheContextType => {
  const context = useContext(PartnerCacheContext);
  if (!context) {
    throw new Error('usePartnerCache must be used within PartnerCacheProvider');
  }
  return context;
};

// Resolves partner.domestic === false ("Увоз") for VAT decisions. Foreign partner => VAT 0%.
// Backed by ensurePartner so a cold cache resolves instead of defaulting to domestic.
export const usePartnerIsImport = (partnerId?: string): boolean => {
  const { getPartner, ensurePartner } = usePartnerCache();
  const [isImport, setIsImport] = useState(false);

  useEffect(() => {
    if (!partnerId) {
      setIsImport(false);
      return;
    }

    const cached = getPartner(partnerId);
    if (cached) {
      setIsImport(cached.domestic === false);
      return;
    }

    let cancelled = false;
    setIsImport(false);
    ensurePartner(partnerId).then(partner => {
      if (!cancelled && partner) setIsImport(partner.domestic === false);
    });
    return () => { cancelled = true; };
  }, [partnerId, getPartner, ensurePartner]);

  return isImport;
};